import { type ReactNode } from 'react';
import type { WidgetType } from './useDashboardStore';
import { useDashboardStore } from './useDashboardStore';
import { cn } from '@/lib/cn';
import {
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

// ── Widget types ──────────────────────────────────────────────
import StatsCard from './widgets/StatsCard';
import RevenueChart from './widgets/RevenueChart';
import ActivityFeed from './widgets/ActivityFeed';
import QuickNotes from './widgets/QuickNotes';
import MiniCalendar from './widgets/MiniCalendar';
import TodoList from './widgets/TodoList';

const WIDGET_MAP: Record<WidgetType, () => ReactNode> = {
  'stats': () => <StatsCard />,
  'revenue-chart': () => <RevenueChart />,
  'activity-feed': () => <ActivityFeed />,
  'quick-notes': () => <QuickNotes />,
  'mini-calendar': () => <MiniCalendar />,
  'todo-list': () => <TodoList />,
};

// Grid span config per widget type
const SPAN_MAP: Record<WidgetType, string> = {
  'stats': 'md:col-span-2 lg:col-span-3',
  'revenue-chart': 'md:col-span-2 lg:col-span-2',
  'activity-feed': 'md:col-span-1 lg:col-span-1',
  'quick-notes': 'md:col-span-1 lg:col-span-1',
  'mini-calendar': 'md:col-span-1 lg:col-span-1',
  'todo-list': 'md:col-span-1 lg:col-span-1',
};

// ── Widget wrapper ────────────────────────────────────────────
interface WidgetProps {
  id: WidgetType;
  label: string;
  isFirst: boolean;
  isLast: boolean;
}

export default function Widget({ id, label, isFirst, isLast }: WidgetProps) {
  const moveWidget = useDashboardStore((s) => s.moveWidget);

  return (
    <div
      className={cn(
        'group relative rounded-xl border border-gray-200 bg-white shadow-sm',
        'transition-all duration-200 ease-in-out',
        'hover:shadow-md hover:border-gray-300',
        'col-span-1',
        SPAN_MAP[id],
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
        <h3 className="text-sm font-medium text-gray-900">{label}</h3>
        <div
          className={cn(
            'flex items-center gap-0.5 opacity-0 group-hover:opacity-100',
            'transition-opacity duration-200',
          )}
        >
          <button
            onClick={() => moveWidget(id, 'up')}
            disabled={isFirst}
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded',
              'text-gray-400 hover:text-gray-900 hover:bg-gray-100',
              'transition-all duration-200 ease-in-out',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
              'disabled:opacity-30 disabled:cursor-not-allowed',
            )}
            aria-label={`Move ${label} up`}
          >
            <ChevronUpIcon className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => moveWidget(id, 'down')}
            disabled={isLast}
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded',
              'text-gray-400 hover:text-gray-900 hover:bg-gray-100',
              'transition-all duration-200 ease-in-out',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
              'disabled:opacity-30 disabled:cursor-not-allowed',
            )}
            aria-label={`Move ${label} down`}
          >
            <ChevronDownIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-5">{WIDGET_MAP[id]()}</div>
    </div>
  );
}
